import React, { useState, useRef, useEffect } from 'react';

import { options as iconOptions } from './icons.js';

import FontAwesomeIcon from '../FontAwesomeIcon';
import { useTranslation } from 'react-i18next';

interface IconOption {
  value: string;
  label: string;
}

interface SelectInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options?: IconOption[];
  placeholder?: string;
  className?: string;
}

export const FontAwesomeIconSelectInput: React.FC<SelectInputProps> = ({
  value,
  onChange,
  options,
  placeholder = '',
  className = '',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const iconList = options || iconOptions;

  const filteredOptions = iconList.filter(
    (opt: IconOption) =>
      opt.label.toLowerCase().includes(filter.toLowerCase()) || opt.value.toLowerCase().includes(filter.toLowerCase())
  );

  const selectedOption = iconList.find((opt: IconOption) => opt.value === value) || null;

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const dropdownElement = containerRef.current;
    if (!dropdownElement) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        e.preventDefault();
      } else if (e.key === 'Enter') {
        if (filteredOptions[highlightedIndex]) {
          onChange(filteredOptions[highlightedIndex].value);
          setIsOpen(false);
        }
        e.preventDefault();
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }
    dropdownElement.addEventListener('keydown', handleKeyDown);
    return () => dropdownElement.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredOptions, highlightedIndex, onChange]);

  return (
    <div className={`relative w-full ${className}`} ref={containerRef} tabIndex={0}>
      <button
        type='button'
        className='w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-0.5 py-2 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 flex items-center'
        onClick={() => {
          setIsOpen((open) => {
            if (!open) {
              setFilter('');
              setHighlightedIndex(0);
            }
            return !open;
          });
        }}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        {selectedOption ? (
          <>
            <FontAwesomeIcon name={selectedOption.value} className='mr-2 text-gray-700' />
            <span className='block truncate text-gray-700'>{selectedOption.label}</span>
          </>
        ) : (
          <span className='block truncate text-gray-400 pl-0.5'>{placeholder}</span>
        )}
        <span className='ml-auto flex items-center pointer-events-none'>
          <svg className='h-5 w-5 text-gray-400' viewBox='0 0 20 20' fill='none' stroke='currentColor'>
            <path d='M7 7l3-3 3 3m0 6l-3 3-3-3' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className='absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto'>
          <div className='flex items-center px-3 py-2 border-b border-gray-200'>
            <FontAwesomeIcon name='search' className='text-gray-400 mr-2' />
            <input
              type='text'
              className='w-full outline-none border-none bg-transparent text-gray-700 pl-1'
              placeholder={t('vip.searchIcon')}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setHighlightedIndex(0);
              }}
              autoFocus
            />
          </div>
          {filteredOptions.length === 0 ? (
            <div className='px-4 py-2 text-gray-500 text-sm'>{t('vip.noIconsFound')}</div>
          ) : (
            <ul tabIndex={-1} role='listbox' className='max-h-48 overflow-y-auto'>
              {filteredOptions.map((option: { value: string; label: string }, idx: number) => (
                <li
                  key={option.value}
                  role='option'
                  aria-selected={value === option.value}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 flex items-center ${
                    idx === highlightedIndex ? 'bg-indigo-600 text-white' : 'text-gray-900'
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  <FontAwesomeIcon
                    name={option.value}
                    className={`mr-2 h-5 w-5 ${idx === highlightedIndex ? 'text-white' : 'text-gray-700'}`}
                  />
                  <span className='block truncate'>{option.label}</span>
                  {value === option.value && (
                    <span
                      className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                        idx === highlightedIndex ? 'text-white' : 'text-indigo-600'
                      }`}
                    >
                      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                      </svg>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default FontAwesomeIconSelectInput;
